import { getConfig } from '@/config';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
export class ApiException extends Error {
    constructor(status, error) {
        const [detail, dict] = Array.isArray(error) ? error : [error];
        const path = (detail.includes('.') ? detail : `errors.api.${detail}`);
        super(path);
        this.status = status;
        this.detail = dict ? [path, dict] : path;
        this.name = 'ApiException';
    }
}
/**
 * Custom hook that provides an authenticated API client.
 * Automatically handles JWT token injection using Auth0's getAccessTokenSilently.
 * Token caching and refresh are handled automatically by the Auth0 SDK.
 */
export const useApi = () => {
    const { getAccessTokenSilently, logout } = useAuth0();
    const config = getConfig();
    const API_BASE_URL = config.REACT_APP_API_URL || 'http://localhost:8000';
    const callApi = useCallback(async (method, endpoint, data) => {
        /* useAuth0 isAuthenticated does not account for session expiration,
         * so ProtectedRoute may not catch. if getAccessTokenSilently fails,
         * assume session is expired and require user to reauthenticate */
        const token = await getAccessTokenSilently().catch(async () => {
            await logout({
                logoutParams: {
                    returnTo: `${window.location.origin}?error=common.sessionExpired`,
                },
            });
            return '';
        });
        if (!token)
            return undefined;
        const response = await fetch(new URL('/api/v1' + endpoint, API_BASE_URL), {
            method,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            credentials: 'include',
            body: data && method !== 'GET' && method !== 'DELETE'
                ? JSON.stringify(data)
                : undefined,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({
                detail: 'errors.generic',
            }));
            const detail = Array.isArray(error.detail)
                ? 'errors.generic'
                : error.detail || error.message || 'errors.generic';
            throw new ApiException(response.status, detail);
        }
        return response.json();
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently, logout]);
    return { callApi };
};
